import { apiFetch } from "../lib/api";

export interface HierarchyScope {
  accountId: string;
  userId: string;
  role?: "owner" | "manager" | "member";
}

export interface HierarchyWorkspace {
  accessRole?: "admin" | "editor" | "viewer";
  id: string;
  accountId: string;
  name: string;
}

export interface HierarchyProject extends HierarchyWorkspace {
  workspaceId: string;
}

export interface HierarchyThread extends HierarchyProject {
  projectId?: string;
}

export interface HierarchySelection {
  accountId?: string;
  workspaceId?: string;
  projectId?: string;
}

export interface HierarchyCatalog {
  canCreateThread?: boolean;
  scope: HierarchyScope;
  workspaces: HierarchyWorkspace[];
  workspace?: HierarchyWorkspace;
  projects: HierarchyProject[];
  project?: HierarchyProject;
  threads: HierarchyThread[];
}

export class HierarchyAccessError extends Error {
  constructor() {
    super("This workspace is unavailable in your current account.");
    this.name = "HierarchyAccessError";
  }
}

export function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new HierarchyAccessError();
  return value as Record<string, unknown>;
}

export function id(value: unknown): string {
  if (typeof value !== "string" || !value || value.length > 512 || /[\s\x00-\x1f\x7f/\\%]/.test(value) || value === "." || value === "..") {
    throw new HierarchyAccessError();
  }
  return value;
}

export async function request(path: string, signal?: AbortSignal): Promise<Record<string, unknown>> {
  const response = await apiFetch(path, { credentials: "include", cache: "no-store", signal });
  if (!response.ok) throw new HierarchyAccessError();
  const body = object(await response.json());
  signal?.throwIfAborted();
  return body;
}

export async function session(signal?: AbortSignal): Promise<HierarchyScope> {
  const body = await request("/code/session", signal);
  if (body.authenticated !== true) throw new HierarchyAccessError();
  const principal = object(body.principal);
  const role = principal.role;
  return { accountId: id(principal.accountId), userId: id(principal.userId),
    ...(role === "owner" || role === "manager" || role === "member" ? { role } : {}) };
}

function list<T extends HierarchyWorkspace>(body: Record<string, unknown>, key: string, validate: (row: Record<string, unknown>) => T): T[] {
  const rows = body[key];
  // Go encodes a nil empty slice as null; an omitted field is malformed.
  if (rows === null) return [];
  if (!Array.isArray(rows)) throw new HierarchyAccessError();
  const result = rows.map((row) => validate(object(row)));
  if (new Set(result.map((row) => row.id)).size !== result.length) throw new HierarchyAccessError();
  return result;
}

function workspace(row: Record<string, unknown>, scope: HierarchyScope): HierarchyWorkspace {
  if (row.accountId !== scope.accountId || typeof row.name !== "string") throw new HierarchyAccessError();
  const accessRole = row.accessRole;
  return { id: id(row.id), accountId: scope.accountId, name: row.name,
    ...(accessRole === "admin" || accessRole === "editor" || accessRole === "viewer" ? { accessRole } : {}) };
}

// No persistent cache: bookmarked selections are hints, never access grants.
// Read ancestry in order and recheck identity before returning private names.
export async function loadHierarchy(selection: HierarchySelection = {}, signal?: AbortSignal): Promise<HierarchyCatalog> {
  for (const value of Object.values(selection)) if (value !== undefined) id(value);
  if (selection.projectId && !selection.workspaceId) throw new HierarchyAccessError();
  const scope = await session(signal);
  if (selection.accountId && selection.accountId !== scope.accountId) throw new HierarchyAccessError();
  const workspaces = list(await request("/code/workspaces", signal), "workspaces", (row) => workspace(row, scope));
  const catalog: HierarchyCatalog = { scope, workspaces, projects: [], threads: [] };
  if (selection.workspaceId) {
    catalog.workspace = workspaces.find((row) => row.id === selection.workspaceId);
    if (!catalog.workspace) throw new HierarchyAccessError();
    const path = `/code/workspaces/${encodeURIComponent(selection.workspaceId)}`;
    catalog.projects = list(await request(`${path}/projects`, signal), "projects", (row) => {
      if (row.workspaceId !== selection.workspaceId) throw new HierarchyAccessError();
      return { ...workspace(row, scope), workspaceId: selection.workspaceId! };
    });
    if (selection.projectId) {
      catalog.project = catalog.projects.find((row) => row.id === selection.projectId);
      if (!catalog.project) throw new HierarchyAccessError();
    }
    const projects = new Set(catalog.projects.map((row) => row.id));
    catalog.threads = list(await request(`${path}/threads`, signal), "threads", (row) => {
      if (row.workspaceId !== selection.workspaceId) throw new HierarchyAccessError();
      const projectId = row.projectId === undefined || row.projectId === "" ? undefined : id(row.projectId);
      if (projectId && !projects.has(projectId)) throw new HierarchyAccessError();
      return { ...workspace(row, scope), workspaceId: selection.workspaceId!, projectId };
    }).filter((row) => row.projectId === selection.projectId);
    const access = (catalog.project ?? catalog.workspace).accessRole;
    catalog.canCreateThread = access === "admin" || access === "editor";
  }
  const current = await session(signal);
  if (current.accountId !== scope.accountId || current.userId !== scope.userId) throw new HierarchyAccessError();
  signal?.throwIfAborted();
  return catalog;
}
